import { baseApi, TAGS } from 'shared/api';
import type { BaseResponse } from 'shared/models/common';
import buildFilesFormData from 'shared/utils/buildFilesFormData';

import { EMPTY_KEY } from './constants';
import type {
  CreateFormBodyRequest,
  CreateFormOptions,
  GetUserCommonCasesResponse,
  TransformedUserCommonCaseResponse,
  UserCommonCase,
} from './types';

export const userCommonCasesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCommonCases: build.query<TransformedUserCommonCaseResponse, string>({
      query: (coopId) => ({
        url: `/user/${coopId}/cases/common`,
        method: 'GET',
      }),
      providesTags: [TAGS.USER_COMMON_CASES],
      transformResponse: (
        response: BaseResponse<GetUserCommonCasesResponse>
      ) => ({
        Cases: response.Data.Cases,
        CaseLevels1: response.Data.CaseLevels1 ?? [],
        CaseLevels2: response.Data.CaseLevels2 ?? [],
      }),
    }),
    getCommonCase: build.query<UserCommonCase, string>({
      query: (caseId) => ({
        url: `/user/cases/common/${caseId}`,
        method: 'GET',
      }),
      transformResponse: (response: BaseResponse<UserCommonCase>) =>
        response.Data,
      serializeQueryArgs: ({ endpointName }) => endpointName,
    }),
    getCommonCaseCreateFormOptions: build.query<CreateFormOptions, string>({
      query: (coopId) => ({
        url: `/user/cases/common/form/create?CooperativeId=${coopId}`,
        method: 'GET',
      }),
      transformResponse: (response: BaseResponse<CreateFormOptions>) => ({
        ...response.Data,
        Spaces:
          response.Data.Spaces?.map((space) => ({
            ...space,
            Value: space.Value ?? EMPTY_KEY,
          })) ?? null,
      }),
    }),
    createUserCommonCase: build.mutation<
      UserCommonCase,
      {
        body: CreateFormBodyRequest;
        files: File[];
      }
    >({
      queryFn: async ({ body, files }, _queryApi, _extra, fetchWithBQ) => {
        const createCommonCaseResult = await fetchWithBQ({
          url: '/user/cases/common',
          method: 'POST',
          body,
        });

        if (createCommonCaseResult.error) {
          return { error: createCommonCaseResult.error };
        }

        const { Data: userCommonCase } =
          createCommonCaseResult.data as BaseResponse<UserCommonCase>;

        if (files.length) {
          const uploadCaseFilesResult = await fetchWithBQ({
            url: `/user/cooperatives/${body.CooperativeId}/documents/incident/${userCommonCase.Id}`,
            method: 'POST',
            body: buildFilesFormData(files),
          });

          if (uploadCaseFilesResult.error) {
            const { Message } = uploadCaseFilesResult.error.data as {
              Message: string;
            };
            const warning = `Selected files were not uploaded: ${Message}`;
            console.warn(warning);
          }
        }

        return {
          data: userCommonCase,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : [TAGS.USER_COMMON_CASES]),
    }),
  }),
});
